import { constant } from './feedback-messenger-window-draft.constant'

export type DraftType = 'email' | 'sms'

export type MessengerDraft = {
	subject: string | null
	content: string
	templateId: number | null
	savedAt: number
}

export type DraftScope = {
	scope: string
	type: DraftType
}

export type DraftIntent = 'restore' | 'fresh' | null

const storageKey = ({ scope, type }: DraftScope) => `${constant.storagePrefix}:${scope}:${type}`

const isExpired = (draft: MessengerDraft) => Date.now() - draft.savedAt > constant.ttlMs

export const loadDraft = (scope: DraftScope): MessengerDraft | null => {
	try {
		const raw = localStorage.getItem(storageKey(scope))
		if (raw === null) {
			return null
		}
		const draft = JSON.parse(raw) as MessengerDraft
		if (
			typeof draft.content !== 'string' ||
			typeof draft.savedAt !== 'number' ||
			(draft.subject !== null && typeof draft.subject !== 'string') ||
			(draft.templateId !== null && typeof draft.templateId !== 'number') ||
			isExpired(draft)
		) {
			localStorage.removeItem(storageKey(scope))
			return null
		}
		return draft
	} catch {
		return null
	}
}

export const removeDraft = (scope: DraftScope) => {
	try {
		localStorage.removeItem(storageKey(scope))
	} catch {
		// storage unavailable — nothing to remove
	}
}

export const pruneDrafts = (dropOldest = false) => {
	try {
		localStorage.removeItem('concept_message')
		const entries: { key: string; savedAt: number }[] = []
		for (let i = 0; i < localStorage.length; i++) {
			const key = localStorage.key(i)
			if (key === null || !key.startsWith(`${constant.storagePrefix}:`)) {
				continue
			}
			try {
				const draft = JSON.parse(localStorage.getItem(key) ?? '') as MessengerDraft
				const valid = typeof draft.savedAt === 'number' && !isExpired(draft)
				entries.push({ key, savedAt: valid ? draft.savedAt : 0 })
			} catch {
				entries.push({ key, savedAt: 0 })
			}
		}
		entries.filter((entry) => entry.savedAt === 0).forEach((entry) => localStorage.removeItem(entry.key))
		if (dropOldest) {
			const valid = entries.filter((entry) => entry.savedAt !== 0).sort((a, b) => a.savedAt - b.savedAt)
			valid.slice(0, Math.ceil(valid.length / 2)).forEach((entry) => localStorage.removeItem(entry.key))
		}
	} catch {
		// storage unavailable
	}
}

export const saveDraft = (scope: DraftScope, draft: MessengerDraft) => {
	const payload = JSON.stringify(draft)
	if (payload.length > constant.maxDraftLength) {
		return
	}
	try {
		localStorage.setItem(storageKey(scope), payload)
	} catch {
		pruneDrafts(true)
		try {
			localStorage.setItem(storageKey(scope), payload)
		} catch {
			// quota still exceeded — skip this save, never break typing
		}
	}
}

export const setIntent = (scope: DraftScope, intent: 'restore' | 'fresh') => {
	try {
		sessionStorage.setItem(constant.intentStorageKey, JSON.stringify({ scope: scope.scope, type: scope.type, intent }))
	} catch {
		// storage unavailable
	}
}

export const consumeIntent = (scope: DraftScope): DraftIntent => {
	try {
		const raw = sessionStorage.getItem(constant.intentStorageKey)
		sessionStorage.removeItem(constant.intentStorageKey)
		if (raw === null) {
			return null
		}
		const parsed = JSON.parse(raw) as { scope?: string; type?: string; intent?: string }
		if (parsed.scope !== scope.scope || parsed.type !== scope.type) {
			return null
		}
		return parsed.intent === 'restore' || parsed.intent === 'fresh' ? parsed.intent : null
	} catch {
		return null
	}
}
